import React, {
  cloneElement,
  PureComponent,
  ReactElement,
  ReactNode,
} from 'react';
import { InjectedFocusableProps, withFocusable } from '../common/Focusable';
import { classes, st } from './ToggleButton.st.css';
import Tooltip from '../Tooltip';
import Text from '../Text';
import { dataHooks, iconChildSize, SHAPES } from './constants';
import { WixDesignSystemContext } from '../WixDesignSystemProvider/context';
import ButtonCore from '../Button/ButtonCore';
import { ToggleButtonProps } from './ToggleButton.types';

type IconProps = InjectedFocusableProps<{
  children: ReactNode;
  hasIcon: boolean;
  size: NonNullable<ToggleButtonProps['size']>;
  shape: ToggleButtonProps['shape'];
  border: boolean;
  labelPlacement: ToggleButtonProps['labelPlacement'];
  className?: string;
}>;

const Icon = ({
  children,
  hasIcon,
  size,
  shape,
  border,
  labelPlacement,
  focusableOnBlur,
  focusableOnFocus,
  className,
}: IconProps) => {
  const iconSize = iconChildSize[size];
  const isLabelOutside =
    shape === 'round' &&
    (labelPlacement === 'end' || labelPlacement === 'start');
  const isLabelStart = labelPlacement === 'start';
  const isLabelEnd =
    labelPlacement === 'end' || (shape === 'pill' && !isLabelStart);

  const [first, second] = React.Children.map(
    children,
    child => child,
  ) as ReactElement[];

  const icon = isLabelStart ? second : first;
  const topOutsideLabel = isLabelOutside && isLabelStart ? first : null;
  const topInsideLabel = !isLabelOutside && isLabelStart ? first : null;
  const bottomInsideLabel = !isLabelOutside && isLabelEnd ? second : null;
  const bottomOutsideLabel = isLabelOutside && isLabelEnd ? second : null;

  // TODO page is scrolled whenever icon focused and we press Space button

  return (
    children && (
      <span className={classes.labelContainer}>
        {topOutsideLabel}
        <div
          className={st(classes.icon, { size, border }, className)}
          onBlur={focusableOnBlur}
          onFocus={focusableOnFocus}
        >
          {topInsideLabel}
          {cloneElement(icon, {
            className: st(
              classes.rootIcon,
              {
                iconOnly: hasIcon && (!first || !second || shape === 'round'),
              },
              icon.props?.className,
            ),
            width: iconSize,
            height: iconSize,
          })}
          {bottomInsideLabel}
        </div>
        {bottomOutsideLabel}
      </span>
    )
  );
};

const ToggleButtonIcon = withFocusable(Icon);

class ToggleButton extends PureComponent<ToggleButtonProps> {
  static displayName = 'ToggleButton';
  static defaultProps = {
    skin: 'standard',
    size: 'medium',
    shape: 'square',
    border: false,
    interactive: true,
    disabled: false,
    labelValue: '',
    labelPlacement: 'tooltip',
    labelEllipsis: false,
    tooltipProps: {
      placement: 'top',
    },
  } as const;

  renderLabel = () => {
    const { disabled, size, labelValue, labelPlacement, labelEllipsis, shape } =
      this.props;

    return (
      <Text
        className={st(classes.label, {
          placement: shape !== SHAPES['pill'] && labelPlacement,
          size,
          shape,
        })}
        disabled={disabled}
        dataHook={dataHooks.label}
        weight="thin"
        ellipsis={labelEllipsis}
      >
        {labelValue}
      </Text>
    );
  };

  render() {
    const {
      children,
      size = ToggleButton.defaultProps.size,
      shape = ToggleButton.defaultProps.shape,
      skin,
      tooltipProps,
      labelValue,
      selected,
      interactive,
      dataHook,
      labelPlacement,
      labelEllipsis,
      disabled,
      border = ToggleButton.defaultProps.border,
      tooltipDisabled,
      className,
      ...rest
    } = this.props;

    const hasIcon = Boolean(children && React.Children.count(children) > 0);

    return (
      <WixDesignSystemContext.Consumer>
        {({ mobile }) => (
          <Tooltip
            className={st(classes.tooltip, { disabled, interactive })}
            {...tooltipProps}
            size="small"
            content={labelValue}
            disabled={
              !labelValue ||
              tooltipDisabled ||
              tooltipProps?.disabled ||
              labelPlacement !== 'tooltip' ||
              shape === 'pill' ||
              mobile
            }
            data-hook={dataHook}
          >
            <ButtonCore
              {...rest}
              type={undefined}
              onFocus={rest?.onFocus as any}
              onBlur={rest?.onBlur as any}
              className={st(
                classes.root,
                {
                  disabled,
                  selected,
                  interactive,
                  skin,
                  labelPlacement,
                  shape,
                  mobile,
                  size,
                  hasIcon,
                },
                className,
              )}
              contentClassName={classes.btnContent}
              data-hook={dataHooks.button}
              data-placement={labelPlacement}
              data-selected={selected}
              data-skin={skin}
              data-shape={shape}
              aria-pressed={selected}
              disabled={disabled}
              aria-label={labelValue?.toString()}
            >
              <ToggleButtonIcon
                hasIcon={hasIcon}
                size={size}
                shape={shape}
                border={border}
                labelPlacement={labelPlacement}
              >
                {labelPlacement === 'start' ? this.renderLabel() : null}
                {children}
                {labelPlacement === 'end' ||
                (shape === SHAPES['pill'] && labelPlacement !== 'start')
                  ? this.renderLabel()
                  : null}
              </ToggleButtonIcon>
              {labelPlacement === 'bottom' && shape !== SHAPES['pill']
                ? this.renderLabel()
                : null}
            </ButtonCore>
          </Tooltip>
        )}
      </WixDesignSystemContext.Consumer>
    );
  }
}
export default ToggleButton;
